import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC } from 'react';
import { MdExpandMore as ExpandMoreIcon } from 'react-icons/md';
import { makeStyles } from 'tss-react/mui';
import { Nullable } from 'common-types';
import {
  ConfigureConnectionInput,
  Connection,
  ConnectionFormFields,
} from '../types/connection-types';
import { setTextField } from '../utils/connection-configure-store-utils';
import InstructionsButton from './InstructionsButton';
import SaveRemoveButtonGroup from './SaveRemoveButtonGroup';

const OktaConnectionForm: FC<
  React.PropsWithChildren<OktaConnectionFormProps>
> = props => {
  const { classes } = useStyles();
  const theme = useTheme();

  const {
    connection,
    connectionFormFields,
    onSaveConnection,
    onRemoveConnection,
    isReadOnlyRole,
    isUpdatable,
    isLoadingSave,
    isLoadingRemove,
    isSelfConnection,
    errorMessage,
  } = props;

  const { domain, clientId, clientSecret } = connectionFormFields;

  const { instructions } = connection;

  return (
    <form className={classes.rootForm}>
      <Box display='flex' flexDirection='column' gap={3}>
        {instructions && (
          <>
            <Accordion
              disableGutters
              variant='outlined'
              sx={{
                maxHeight: 190,
                overflowY: 'scroll',
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography color={theme.palette.info.main} variant='overline'>
                  {instructions.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {instructions.details.map((step, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={step}
                        primaryTypographyProps={{
                          variant: 'caption',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <InstructionsButton instructions={instructions} />
          </>
        )}
        <Box
          display='flex'
          flexDirection='column'
          gap={2}
          alignItems='flex-start'
          mb={2}>
          <TextField
            autoComplete='off'
            size='small'
            required
            type='text'
            variant='outlined'
            label='Okta Domain'
            name='domain'
            value={domain}
            onChange={setTextField}
            fullWidth
            disabled={isReadOnlyRole}
          />
          <TextField
            autoComplete='off'
            size='small'
            required
            type='text'
            variant='outlined'
            label='Client ID'
            name='clientId'
            value={clientId}
            onChange={setTextField}
            fullWidth
            disabled={isReadOnlyRole}
          />
          <TextField
            autoComplete='off'
            size='small'
            required
            type='password'
            variant='outlined'
            label='Client Secret'
            name='clientSecret'
            value={clientSecret}
            onChange={setTextField}
            fullWidth
            disabled={isReadOnlyRole}
          />
        </Box>
      </Box>
      <SaveRemoveButtonGroup
        connection={connection}
        connectionFormFields={connectionFormFields}
        onSaveConnection={onSaveConnection}
        onRemoveConnection={onRemoveConnection}
        isReadOnlyRole={isReadOnlyRole}
        isUpdatable={isUpdatable}
        isLoadingSave={isLoadingSave}
        isLoadingRemove={isLoadingRemove}
        isSelfConnection={isSelfConnection}
      />
      {errorMessage && (
        <Box flexGrow={1} mt={1} textAlign='end'>
          <Typography color='error' variant='body2'>
            {errorMessage}
          </Typography>
        </Box>
      )}
    </form>
  );
};

const useStyles = makeStyles()({
  rootForm: {
    width: '100%',
  },
});

export interface OktaConnectionFormProps {
  connection: Connection;
  connectionFormFields: ConnectionFormFields;
  onSaveConnection: (
    connectionId: string,
    connectionFormFields: ConnectionFormFields,
  ) => void;
  onRemoveConnection: (
    configureConnectionInput: ConfigureConnectionInput,
  ) => void;
  isReadOnlyRole: boolean;
  isUpdatable: boolean;
  isLoadingSave: boolean;
  isLoadingRemove: boolean;
  isSelfConnection: boolean;
  errorMessage: Nullable<string>;
}

export default OktaConnectionForm;
